<script setup lang="ts">
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '~/components/ui/table'
import { RATE_PLACEHOLDER } from '~/data/pages/rates/consts'

const props = defineProps<{
  currency: string
}>()

const currencyStore = useCurrencyStore()
const { list } = storeToRefs(currencyStore)

const selectedCurrency = computed(() =>
  list.value.find((i) => i.code.toLowerCase() === props.currency)
)

const { data } = await useFetch('/api/rates/widgets-cached')

const currencyBuy = computed(() => {
  const raw = data.value?.bestRatesToday[`buy_${props.currency}`]
  const num = parseFloat(raw ?? '')
  return isNaN(num) ? RATE_PLACEHOLDER : num.toFixed(2)
})

const currencySell = computed(() => {
  const raw = data.value?.bestRatesToday[`sell_${props.currency}`]
  const num = parseFloat(raw ?? '')
  return isNaN(num) ? RATE_PLACEHOLDER : num.toFixed(2)
})

const centralBankRates = computed(() => data.value?.centralBankRates[props.currency])
</script>

<template>
  <Table class="w-full overflow-hidden rounded-xl border border-sky-800">
    <TableHeader>
      <TableRow>
        <TableHead
          rowspan="2"
          class="border-none bg-sky-600 px-2 py-2 text-center sm:px-4 sm:py-3"
        />
        <TableHead
          rowspan="2"
          class="bg-sky-700 px-2 py-2 text-center text-base !text-slate-100 sm:px-4 sm:py-3 sm:!text-lg"
        >
          {{ $t('widget.rates.currency_best.nbkr_rate') }}
        </TableHead>
        <TableHead
          colspan="2"
          class="bg-sky-600 px-2 py-2 text-center text-base !text-slate-100 sm:px-4 sm:py-3 sm:!text-lg"
        >
          {{ $t('widget.rates.currency_best.best_rates') }}
        </TableHead>
      </TableRow>
      <TableRow>
        <TableHead class="bg-sky-600 px-2 py-2 text-center text-sm sm:px-4 sm:py-3">
          <div class="!text-slate-200">{{ $t('common.buy') }}</div>
        </TableHead>
        <TableHead class="bg-sky-600 px-2 py-2 text-center text-sm sm:px-4 sm:py-3">
          <div class="!text-slate-200">{{ $t('common.sell') }}</div>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <TableRow>
        <TableCell
          class="bg-sky-700 px-2 py-2 text-center text-base font-medium uppercase text-cyan-400 sm:px-4 sm:py-3 sm:text-xl"
        >
          {{ props.currency }}
        </TableCell>
        <TableCell
          class="bg-sky-800 px-2 py-2 text-center font-semibold text-slate-100 sm:px-4 sm:py-3"
        >
          <i18n-t
            keypath="widget.rates.currency_best.som_per"
            tag="div"
            class="text-[10px] sm:text-xs"
          >
            <template #amount>
              <div class="text-base sm:text-xl">{{ centralBankRates ?? RATE_PLACEHOLDER }}</div>
            </template>

            <template #currency_title>
              {{ selectedCurrency?.title || '' }}
            </template>
          </i18n-t>
        </TableCell>
        <TableCell
          class="bg-sky-700 px-2 py-2 text-center text-base font-semibold text-slate-100 sm:px-4 sm:py-3 sm:text-xl"
        >
          {{ currencyBuy }}
        </TableCell>
        <TableCell
          class="bg-sky-700 px-2 py-2 text-center text-base font-semibold text-slate-100 sm:px-4 sm:py-3 sm:text-xl"
        >
          {{ currencySell }}
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
